import { LESSON_STATUS } from '@yandex-int/k-common/typings';
import b from 'b_';
import { Popup, PopupType, ThreeDots } from 'platform-components';
import { POPUP_DIRECTIONS } from 'platform-components/constants';
import { getIsContentManager } from 'platform-components/src/components/user/selectors';
import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';

import * as lessonSelectors from 'common.components/lesson/selectors';
import { getIsTeacherFromMes } from 'utils/external-lms/external-lms';

import { SidePanelControlsTypeAddLessonToCourse } from './_type/side-panel__controls_type_add-lesson-to-course';
import { SidePanelControlsTypeAddProblemById } from './_type/side-panel__controls_type_add-problem-by-id';
import { SidePanelControlsTypeCopyLesson } from './_type/side-panel__controls_type_copy-lesson';
import { SidePanelControlsTypeCopyLessonLink } from './_type/side-panel__controls_type_copy-lesson-link';
import { SidePanelControlsTypeCopyLinkCm } from './_type/side-panel__controls_type_copy-link-cm';
import { SidePanelControlsTypeCreateProblem } from './_type/side-panel__controls_type_create-problem';
import { SidePanelControlsTypeDeleteLesson } from './_type/side-panel__controls_type_delete-lesson';
import { SidePanelControlsTypeGotoCustomFlowLessonEditor } from './_type/side-panel__controls_type_goto-custom-flow-lesson-editor';
import { SidePanelControlsTypeUnsubscribe } from './_type/side-panel__controls_type_unsubscribe';
import { SidePanelControlsItem } from './side-panel__controls-item';
import {
    DotPopupItemName,
    DotPopupItem,
    GetDotPopupItemsArgs,
    SidePanelControlsProps,
    DotPopupDisableItems,
} from './typings';

import './side-panel__controls.scss';

function getDotPopupItems(args: GetDotPopupItemsArgs) {
    const { lessonStatus, isContentManager, isMobile, isPreparedLesson, isCopyingEnabled, hidePopup, disableItems } =
        args;

    const withBorder = true;
    const dotPopupItems: DotPopupItem[] = [];
    const canCopyLessonLinkCm = isContentManager && !isPreparedLesson;
    const canEditLesson = lessonStatus === LESSON_STATUS.NOT_ASSIGNED || lessonStatus === LESSON_STATUS.ASSIGNED;

    dotPopupItems.push({
        name: 'copyLessonLink',
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeCopyLessonLink
                isMobile={Boolean(isMobile)}
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
            />
        ),
    });

    dotPopupItems.push({
        name: 'unsubscribe',
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeUnsubscribe
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
                onUnsubscribe={hidePopup}
            />
        ),
    });

    dotPopupItems.push({
        name: 'copyCLesson',
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeCopyLesson
                isCopyingEnabled={isCopyingEnabled}
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
                onCopyLesson={hidePopup}
            />
        ),
    });

    dotPopupItems.push({
        name: 'copyLesson',
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeAddLessonToCourse
                isCopyingEnabled={isCopyingEnabled}
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
            />
        ),
    });

    dotPopupItems.push({
        name: 'copyLessonLinkCM',
        withBorder,
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeCopyLinkCm
                canCopyLinkCm={canCopyLessonLinkCm}
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
                onCopyLink={hidePopup}
            />
        ),
    });

    dotPopupItems.push({
        name: 'createProblem',
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeCreateProblem
                canCreateProblems={canCopyLessonLinkCm && canEditLesson}
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
            />
        ),
    });

    dotPopupItems.push({
        name: 'addProblemById',
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeAddProblemById
                canAddProblemById={canCopyLessonLinkCm && canEditLesson}
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
            />
        ),
    });

    dotPopupItems.push({
        name: 'gotoCustomFlowLessonEditor',
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeGotoCustomFlowLessonEditor
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
            />
        ),
    });

    dotPopupItems.push({
        name: 'deleteLesson',
        withBorder,
        render: ({ notifyCanBeRender, notifyCanNotBeRender }) => (
            <SidePanelControlsTypeDeleteLesson
                notifyCanBeRender={notifyCanBeRender}
                notifyCanNotBeRender={notifyCanNotBeRender}
            />
        ),
    });

    return dotPopupItems.filter((item) => !disableItems?.[item.name]);
}

export const Controls = (props: SidePanelControlsProps) => {
    const { search } = useLocation();
    const isPreparedLesson = useSelector(lessonSelectors.getIsPreparedLesson);
    const isContentManager = useSelector(getIsContentManager);
    const lessonStatus = useSelector(lessonSelectors.getLessonStatus)!;
    const { isMobile, isCopyingEnabled } = props;
    const popupRef = useRef<PopupType | null>(null);
    const hidePopup = useCallback(() => {
        popupRef.current?.hide();
    }, []);
    const setPopupRef = useCallback((ref: Popup | null) => {
        popupRef.current = ref ? ref : null;
    }, []);

    const isTeacherFromMes = getIsTeacherFromMes(search);

    // т.к. отсутствует обратная синхронизация, блокируем удаление заданий, созданных через внешние источник
    const dotPopupDisabledItems: DotPopupDisableItems = { deleteLesson: isTeacherFromMes };

    const dotPopupItems = useMemo(() => {
        return getDotPopupItems({
            lessonStatus,
            isContentManager,
            isMobile,
            isPreparedLesson,
            isCopyingEnabled,
            hidePopup,
            disableItems: dotPopupDisabledItems,
        });
    }, [lessonStatus, isContentManager, isMobile, isPreparedLesson, isCopyingEnabled, hidePopup]);

    const initRenderControls = useMemo(() => dotPopupItems.map(({ name }) => name), [dotPopupItems]);

    const [renderControls, setRenderControls] = useState<DotPopupItemName[]>(initRenderControls);
    const addRenderControl = useCallback(
        (name: DotPopupItemName) => {
            if (renderControls.includes(name)) {
                return;
            }
            const newRenderControls = [...renderControls, name];
            setRenderControls(newRenderControls);
        },
        [renderControls]
    );
    const removeRenderControl = useCallback(
        (name: DotPopupItemName) => {
            if (!renderControls.includes(name)) {
                return;
            }
            const newRenderControls = renderControls.filter((n) => n !== name);
            setRenderControls(newRenderControls);
        },
        [renderControls]
    );
    const hiddenControl = renderControls.length === 0;

    return (
        <ThreeDots
            direction={POPUP_DIRECTIONS.LEFT}
            mix={b('side-panel', 'controls-dots', { hidden: hiddenControl })}
            popupProps={{
                mix: 'side-panel__controls-popup',
                inLayer: false,
                ref: setPopupRef,
                fitToScreen: true,
            }}
            view={ThreeDots.VIEWS.PSEUDO}
        >
            {dotPopupItems.map(({ name, render, withBorder }) => (
                <SidePanelControlsItem
                    addRenderControl={addRenderControl}
                    key={name}
                    name={name}
                    removeRenderControl={removeRenderControl}
                    render={render}
                    shouldShowItem={renderControls.includes(name)}
                    withBorder={withBorder}
                />
            ))}
        </ThreeDots>
    );
};
